import * as React from "react";
import styled from "@emotion/styled";
import { Navigate, useLocation } from "react-router-dom";
import { scrollToTop } from "utils/scroll";
import {
  Button,
  Checkbox,
  FlexRow,
  FieldError,
  Form,
  H1,
  Label,
  Legend,
  Link,
  Radio,
  TagCheckbox,
  Text,
  TextArea,
  SiteContainer,
  Select,
  AllCaps,
} from "atoms";
import reactionMustHugSrc from "atoms/icons/reaction_must-hug_no-bg.svg";
import reactionYaySrc from "atoms/icons/reaction_yay_no-bg.svg";
import reactionMehSrc from "atoms/icons/reaction_meh_no-bg.svg";
import reactionNahSrc from "atoms/icons/reaction_nah_no-bg.svg";
import { constants } from "config";
import { useAuth } from "features/auth";
import { useProfilePoints } from "features/profile";
import { ReviewableSummary } from "features/reviewable/components/ReviewableSummary";
import { Project } from "features/reviewable/types";
import { fieldOptions, validators } from "utils/form";
import { ProjectReview } from "../types";
import { useProjectReviewForm } from "../api/useProjectReviewForm";
import { useReview } from "../api/useReview";

import reviewStatusOptions from "../data/reviewStatusOptions.json";

type ProjectReviewProps = {
  projectId: string;
  project?: Project;
  review?: ProjectReview;
};

const ratingIcons = {
  Nah: reactionNahSrc,
  Meh: reactionMehSrc,
  Yay: reactionYaySrc,
  "Must Hug": reactionMustHugSrc,
};
type RatingLabel = keyof typeof ratingIcons;

const founderTeamTags = [
  "Doxxed and Dependable",
  "Mission-Oriented",
  "Engaged and Present",
  "Seasoned Storytellers",
  "DEI Innovators",
  "Brand Builders",
  "Upfront and Transparent",
  "Real Life Activists",
];

const communityTags = [
  "Beginner Friendly",
  "Creative Souls",
  "Rockstar Mods",
  "Diamond Hands",
  "Passionate Changemakers",
  "Real Humans, Real Connections",
  "Representation Matters",
  "Education Enthusiasts",
];

const roadmapTags = [
  "Long Term Vision",
  "Innovative Ideas",
  "Charitable Causes",
  "IRL Utility",
  "Metaverse Magic",
  "Premium Partnerships",
  "Community First",
  "All About The Art",
];

const artTags = [
  "Sparks Joy",
  "Original and Creative",
  "Dark and Deadly",
  "Retro Appeal",
  "Bright and Bold",
  "Thought-Provoking",
  "Detailed and Intricate",
  "Museum-Worthy",
];

const textFieldRequirements = {
  ...fieldOptions.required,
  ...fieldOptions.maxLength(1000),
};

function ProjectReviewForm({ projectId, project, review }: ProjectReviewProps) {
  const [reviewCompleted, setReviewCompleted] = React.useState<boolean>(false);

  const {
    user,
    roles: { canReview, showAdminFeatures },
  } = useAuth();

  const location = useLocation();

  const { data: balance } = useProfilePoints(user?.id ?? "");
  const canStakePoints =
    !!balance && balance.pointsBalance >= constants.pointReviewCost;

  // Check database - Rejected reviews are not in useReviews
  const { data: existingReview } = useReview(
    "PROJECT",
    projectId,
    user?.id || ""
  );

  const {
    register,
    formState: { errors, isSubmitSuccessful, isSubmitting },
    onSubmit,
    mutation: { isError: isSubmitError },
    watching: {
      artTagValues,
      communityTagValues,
      founderTeamTagValues,
      roadmapTagValues,
    },
  } = useProjectReviewForm(projectId, review);

  // Set the state when the review is successful. Otherwise the query hooks
  // refetch and the user gets an error that they have already reviewed. We
  // also need to scroll to the top in case the user scrolled down to view the
  // review.
  React.useEffect(() => {
    if (!isSubmitError && isSubmitSuccessful) {
      setReviewCompleted(true);
      scrollToTop();
    }
  }, [isSubmitError, isSubmitSuccessful]);

  if (reviewCompleted) {
    const newTotalApproved =
      (balance?.lifetimeEarnings || 0) + constants.pointsEarnedReviewApproved;

    return (
      <Main>
        {review ? (
          <ReviewHeader>
            <H1>The review has been updated</H1>

            <ReturnLink
              variant="primary"
              arrow="dr"
              to={`/projects/${projectId}`}
            >
              Return to Project
            </ReturnLink>
          </ReviewHeader>
        ) : (
          <ReviewHeader>
            <H1>Thank you for your&nbsp;review!</H1>

            <Text>
              You have spent {constants.pointReviewCost} <AllCaps>Hug</AllCaps>{" "}
              Points to submit your review.
            </Text>

            <Text>
              If your review is approved, you will receive{" "}
              {constants.pointsEarnedReviewApproved} <AllCaps>Hug</AllCaps>{" "}
              Points for a lifetime total of {newTotalApproved}.
            </Text>

            <ReturnLink
              variant="primary"
              arrow="dr"
              to={`/projects/${projectId}`}
            >
              Return to Project
            </ReturnLink>
          </ReviewHeader>
        )}
        <div />
      </Main>
    );
  }

  if (!review && existingReview) {
    return (
      <Main>
        <ReviewHeader>
          <Text>
            {existingReview.status === "REJECTED"
              ? "You already have a rejected review for this project."
              : "You have already reviewed this project."}
          </Text>
          <Text>
            <ReturnLink
              variant="primary"
              arrow="dr"
              to={`/projects/${projectId}`}
            >
              Return to Project
            </ReturnLink>
          </Text>
        </ReviewHeader>
      </Main>
    );
  }

  if (!canReview) {
    return <Navigate to="/onboarding" replace state={{ from: location }} />;
  }

  return (
    <Main>
      {review ? (
        <ReviewHeader>
          <H1>Update Review</H1>
          <ReviewableSummary
            reviewable={project}
            showDescription={false}
            inline
          />
        </ReviewHeader>
      ) : (
        <ReviewHeader>
          <H1>Write a Review</H1>
          <ReviewableSummary
            reviewable={project}
            showDescription={false}
            inline
          />

          {canStakePoints ? (
            <>
              <Text size="sm">
                Share your thoughts on {project?.name} below.
              </Text>
              <Text size="sm">
                Submitting a review costs {constants.pointReviewCost}{" "}
                <AllCaps>Hug</AllCaps> Points - don&apos;t worry, you&apos;ll
                earn it all back (and more!) once your review is approved.
              </Text>
            </>
          ) : (
            <>
              <SText size="sm">
                Uh oh! You don&apos;t have enough H<AllCaps>Hug</AllCaps>UG
                Points to write a review at the moment.
              </SText>

              <SText size="sm">
                <strong>Current Balance:</strong> {balance?.pointsBalance ?? 0}
              </SText>

              <ReturnLink
                variant="primary"
                arrow="dr"
                to={`/projects/${projectId}`}
              >
                Return to Project
              </ReturnLink>
            </>
          )}
        </ReviewHeader>
      )}

      {canStakePoints && (
        <SForm onSubmit={onSubmit}>
          {showAdminFeatures && (
            <Label>
              Review Status
              <Select {...register("status", { ...fieldOptions.required })}>
                {reviewStatusOptions.map((status) => (
                  <option key={status} value={status.toUpperCase()}>
                    {status}
                  </option>
                ))}
              </Select>
            </Label>
          )}

          <Fieldset>
            <Legend>
              How would you rate this project?{" "}
              <RequiredLabel>(Required)</RequiredLabel>
            </Legend>
            <FlexRow
              flexWrap="nowrap"
              justifyContent="flex-start"
              alignItems="flex-start"
            >
              {Object.keys(ratingIcons).map((key) => (
                <RatingRadio
                  {...register("rating", { ...fieldOptions.required })}
                  label={key}
                  value={key}
                  key={key}
                />
              ))}
            </FlexRow>

            {errors.rating && <FieldError>Please choose a rating.</FieldError>}
          </Fieldset>

          <Label>
            This project may be for you if...{" "}
            <RequiredLabel>(Required)</RequiredLabel>
            <TextArea
              {...register("forYouIf", textFieldRequirements)}
              isInvalid={!!errors.forYouIf}
              aria-invalid={!!errors.forYouIf}
            />
            {errors.forYouIf && (
              <FieldError>{errors.forYouIf.message}</FieldError>
            )}
          </Label>

          <Label>
            This project may NOT be for you if...{" "}
            <RequiredLabel>(Required)</RequiredLabel>
            <TextArea
              {...register("notForYouIf", textFieldRequirements)}
              isInvalid={!!errors.notForYouIf}
              aria-invalid={!!errors.notForYouIf}
            />
            {errors.notForYouIf && (
              <FieldError>{errors.notForYouIf.message}</FieldError>
            )}
          </Label>

          <Fieldset>
            <Legend>
              Describe the Founder or Team
              <br />
              <small>Select up to 2</small>
            </Legend>
            {founderTeamTags.map((value) => (
              <TagCheckbox
                variant="founder"
                {...register("founderTeamTags", {
                  validate: validators.limitReviewTags,
                })}
                label={value}
                value={value}
                key={value}
                disabled={
                  founderTeamTagValues?.length >= 2 &&
                  !founderTeamTagValues?.includes(value)
                }
              />
            ))}
            {errors.founderTeamTags && (
              <FieldError block>{errors.founderTeamTags.message}</FieldError>
            )}
          </Fieldset>

          <Fieldset>
            <Legend>
              Describe the Community
              <br />
              <small>Select up to 2</small>
            </Legend>
            {communityTags.map((value) => (
              <TagCheckbox
                variant="community"
                {...register("communityTags", {
                  validate: validators.limitReviewTags,
                })}
                label={value}
                value={value}
                key={value}
                disabled={
                  communityTagValues?.length >= 2 &&
                  !communityTagValues?.includes(value)
                }
              />
            ))}
            {errors.communityTags && (
              <FieldError block>{errors.communityTags.message}</FieldError>
            )}
          </Fieldset>

          <Fieldset>
            <Legend>
              Describe the Roadmap and Business Plan
              <br />
              <small>Select up to 2</small>
            </Legend>
            {roadmapTags.map((value) => (
              <TagCheckbox
                variant="roadmap"
                {...register("roadmapTags", {
                  validate: validators.limitReviewTags,
                })}
                label={value}
                value={value}
                key={value}
                disabled={
                  roadmapTagValues?.length >= 2 &&
                  !roadmapTagValues?.includes(value)
                }
              />
            ))}
            {errors.roadmapTags && (
              <FieldError block>{errors.roadmapTags.message}</FieldError>
            )}
          </Fieldset>

          <Fieldset>
            <Legend>
              Describe the Art
              <br />
              <small>Select up to 2</small>
            </Legend>
            {artTags.map((value) => (
              <TagCheckbox
                variant="art"
                {...register("artTags", {
                  validate: validators.limitReviewTags,
                })}
                label={value}
                value={value}
                key={value}
                disabled={
                  artTagValues?.length >= 2 && !artTagValues?.includes(value)
                }
              />
            ))}
            {errors.artTags && (
              <FieldError block>{errors.artTags.message}</FieldError>
            )}
          </Fieldset>

          <InlineQuestion>
            I have minted this project
            <FlexRow>
              <Checkbox label="Yes" {...register("reviewerMinted")} />
            </FlexRow>
          </InlineQuestion>

          <InlineQuestion>
            I currently hold this project
            <FlexRow>
              <Checkbox label="Yes" {...register("reviewerHolding")} />
            </FlexRow>
          </InlineQuestion>

          <Fieldset>
            <Legend>
              How would you like your name to be displayed on the review?
            </Legend>

            <Radio
              {...register("isAnonymous")}
              value="false"
              label={`Use my username (${
                review ? review.displayName : user?.name
              })`}
            />
            <Radio
              {...register("isAnonymous")}
              value="true"
              label="Hide my username (Anonymous user)"
            />
          </Fieldset>

          <div>
            <Button
              type="submit"
              variant="primary"
              disabled={isSubmitting}
              arrow="r"
            >
              Submit Review
            </Button>

            {isSubmitError && (
              <FieldError block>
                Sorry, something went wrong. Try again.
              </FieldError>
            )}

            <SText size="xs">
              <em>
                By submitting this review, I confirm this review is based on my
                own personal experience, and agree to spend{" "}
                {constants.pointReviewCost} HUG Points.
              </em>
            </SText>
          </div>
        </SForm>
      )}
    </Main>
  );
}

const Main = styled(SiteContainer)({
  minHeight: "80vh",
}).withComponent("main");

const Fieldset = styled(Label)({}).withComponent("fieldset");

const ReviewHeader = styled.div({
  padding: "0 0 2rem ",
});

const SForm = styled(Form)({
  gap: "4rem",
});

const InlineQuestion = styled(Label)(({ theme }) => ({
  ...theme.fonts.body,
  alignItems: "center",
  display: "inline-flex",
  fontWeight: "700",
  gap: "1.5rem",
  maxWidth: "max-content",
})).withComponent("div");

const SText = styled(Text)({
  margin: "1em 0",
});

const RatingRadio = styled(Radio)(({ label, theme }) => ({
  position: "relative",
  flexDirection: "column",
  alignItems: "center",
  flex: "1 1 70px",
  gap: "10px",
  maxWidth: "90px",
  textAlign: "center",
  justifyContent: "flex-start",
  input: {
    border: `2px solid ${theme.colors.fg20}`,
    height: 60,
    width: 60,
    "@media (prefers-reduced-motion: no-preference)": {
      transition: "all 0.15s ease",
    },
    backgroundColor: theme.colors.bg,
  },
  "input:hover, input:focus-visible": {
    backgroundColor: theme.colors.bg,
    border: `2px solid ${theme.colors.fg}`,
  },
  "input::before": {
    all: "unset",
    backgroundImage: `url("${ratingIcons[label as RatingLabel]}")`,
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    backgroundSize: "contain",
    content: "''",
    clipPath: "none",
    width: 50,
    height: 50,
    pointerEvents: "none",
    transform: "translate(2%, 2%)",
    "@media (prefers-reduced-motion: no-preference)": {
      transition: "all 0.15s ease",
    },
  },
  "input:checked": {
    backgroundColor: theme.colors.bg,
  },
  div: {
    ...theme.fonts.body,
    border: "1px solid rgba(0,0,0,0)",
    borderRadius: theme.borderRadius.round,
    fontSize: "14px",
    padding: "2px 8px 1px",
    "@media (prefers-reduced-motion: no-preference)": {
      transition: "all 0.15s ease",
    },
  },
  "input:checked + div": {
    borderColor: theme.colors.fg,
  },
  "@media (min-width: 400px)": {
    input: {
      height: 80,
      width: 80,
    },
    "input::before": {
      width: 60,
      height: 60,
    },
  },
}));

const RequiredLabel = styled.small(({ theme }) => ({
  color: theme.colors.error,
  fontStyle: "italic",
}));
const ReturnLink = styled(Link)({
  margin: "2rem 0",
});

export { ProjectReviewForm };
